import {Controller} from 'stimulus';

export default class extends Controller {
  static targets = ['priceField', 'collapseSection', 'collapseButton',
    'warningBanner', 'validationError', 'extraSlotsToggle', 'extraSlotsRow',
    'notesSection', 'imagesSection',
    'imageSlot1', 'imageSlot2', 'imageSlot3',
    'requiredLabel1', 'requiredLabel2', 'requiredLabel3'];

  static values = {
    threshold: Number,
    image2Required: Boolean,
    image3Required: Boolean,
  };

  connect() {
    this.visibleExtraRows = 0;
    this.imagesMoved = false;
    if (this.hasCollapseButtonTarget) {
      this.defaultButtonText = this.collapseButtonTarget.textContent.trim();
    }
    this.checkPrice();
  }

  priceChanged() {
    this.checkPrice();
  }

  checkPrice() {
    const price = parseFloat(
        this.priceFieldTarget.value.replace(',', '.')) || 0;
    const isHighValue = price >= this.thresholdValue;

    this.toggleRequiredIndicators(isHighValue);
    this.toggleWarningBanner(isHighValue);
    this.toggleExtraSlots(isHighValue);
    this.moveImagesSection(isHighValue);
    this.hideValidationError();
  }

  toggleRequiredIndicators(required) {
    this.toggleImageSlot(this.imageSlot1Target, this.requiredLabel1Target,
        required);

    if (this.image2RequiredValue) {
      this.toggleImageSlot(this.imageSlot2Target, this.requiredLabel2Target,
          required);
    }

    if (this.image3RequiredValue) {
      this.toggleImageSlot(this.imageSlot3Target, this.requiredLabel3Target,
          required);
    }
  }

  toggleImageSlot(slotTarget, labelTarget, required) {
    if (required) {
      slotTarget.classList.add('image-required');
      labelTarget.classList.remove('d-none');
    } else {
      slotTarget.classList.remove('image-required');
      labelTarget.classList.add('d-none');
    }
  }

  toggleWarningBanner(visible) {
    if (!this.hasWarningBannerTarget) return;

    if (visible) {
      this.warningBannerTarget.classList.remove('d-none');
    } else {
      this.warningBannerTarget.classList.add('d-none');
    }
  }

  toggleExtraSlots(visible) {
    if (!this.hasExtraSlotsToggleTarget) return;

    if (visible) {
      if (this.visibleExtraRows < this.extraSlotsRowTargets.length) {
        this.extraSlotsToggleTarget.classList.remove('d-none');
      }
    } else {
      this.extraSlotsToggleTarget.classList.add('d-none');
      this.extraSlotsRowTargets.forEach((row) => row.classList.add('d-none'));
      this.visibleExtraRows = 0;
    }
  }

  showExtraSlots() {
    if (!this.hasExtraSlotsRowTarget) return;

    const rows = this.extraSlotsRowTargets;
    if (this.visibleExtraRows < rows.length) {
      rows[this.visibleExtraRows].classList.remove('d-none');
      this.visibleExtraRows++;
    }

    if (this.visibleExtraRows >= rows.length) {
      this.extraSlotsToggleTarget.classList.add('d-none');
    }
  }

  imageChanged(event) {
    const fileInput = event.target;
    if (fileInput.type !== 'file' || !fileInput.files.length) return;

    const match = fileInput.name.match(/image(\d+)/);
    if (!match) return;

    const imageNumber = parseInt(match[1]);
    if (imageNumber % 3 === 0 && imageNumber < 12) {
      const rowIndex = (imageNumber / 3) - 1;
      this.revealExtraRow(rowIndex);
    }
  }

  revealExtraRow(index) {
    const rows = this.extraSlotsRowTargets;
    if (index >= rows.length || !rows[index].classList.contains('d-none')) {
      return;
    }

    rows[index].classList.remove('d-none');
    this.visibleExtraRows = Math.max(this.visibleExtraRows, index + 1);

    if (this.visibleExtraRows >= rows.length) {
      this.extraSlotsToggleTarget.classList.add('d-none');
    }
  }

  moveImagesSection(aboveNotes) {
    if (!this.hasImagesSectionTarget || !this.hasNotesSectionTarget) return;

    if (aboveNotes && !this.imagesMoved) {
      this.notesSectionTarget.before(this.imagesSectionTarget);
      this.imagesMoved = true;
    } else if (!aboveNotes && this.imagesMoved) {
      this.collapseSectionTarget.querySelector('.premium-fields')
          .appendChild(this.imagesSectionTarget);
      this.imagesMoved = false;
    }

    this.updateCollapseButtonText(aboveNotes);
  }

  updateCollapseButtonText(short) {
    if (!this.hasCollapseButtonTarget) return;

    if (short) {
      this.collapseButtonTarget.textContent =
          this.collapseButtonTarget.dataset.imageRequirementShortText;
    } else {
      this.collapseButtonTarget.textContent = this.defaultButtonText;
    }
  }

  validateImages(event) {
    const price = parseFloat(
        this.priceFieldTarget.value.replace(',', '.')) || 0;
    if (price < this.thresholdValue) return;

    const missing = [];

    if (!this.hasFileSelected(this.imageSlot1Target)) {
      missing.push('1');
    }

    if (this.image2RequiredValue &&
        !this.hasFileSelected(this.imageSlot2Target)) {
      missing.push('2');
    }

    if (this.image3RequiredValue &&
        !this.hasFileSelected(this.imageSlot3Target)) {
      missing.push('3');
    }

    if (missing.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      this.showValidationError(missing);
    }
  }

  showValidationError(missing) {
    if (!this.hasValidationErrorTarget) return;

    const template = this.validationErrorTarget.dataset.messageTemplate || '';
    const labels = missing.map((n) =>
      this.validationErrorTarget.dataset[`label${n}`] || `Afbeelding ${n}`);
    this.validationErrorTarget.textContent =
        template.replace('%{labels}', labels.join(', '));
    this.validationErrorTarget.classList.remove('d-none');
    this.validationErrorTarget.scrollIntoView(
        {behavior: 'smooth', block: 'center'});
  }

  hideValidationError() {
    if (!this.hasValidationErrorTarget) return;

    this.validationErrorTarget.classList.add('d-none');
  }

  hasFileSelected(slotElement) {
    const fileInput = slotElement.querySelector('input[type="file"]');
    return fileInput && fileInput.files && fileInput.files.length > 0;
  }
}
